import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { useSEO } from "@/lib/seo";
import { api } from "@/lib/api";
import { Field, Input, Select } from "@/components/site/Fields";
import { Reveal } from "@/components/site/Motion";
import { VehicleCard } from "@/components/site/VehicleCard";

const KEYS = ["make", "model", "year_min", "year_max", "price_max", "mileage_max", "status", "vehicle_type"];
const TYPES = ["coupe", "convertible", "suv", "sedan", "hypercar", "classic", "gt", "track"];

export default function Vehicles() {
  const { t, lang } = useLang();
  const [params, setParams] = useSearchParams();
  const [f, setF] = useState(() => Object.fromEntries(KEYS.map((k) => [k, params.get(k) || ""])));
  useEffect(() => {
    const p = {};
    KEYS.forEach((k) => f[k] && (p[k] = f[k]));
    setParams(p, { replace: true });
  }, [f, setParams]);
  const query = useMemo(() => Object.fromEntries(Object.entries(f).filter(([, v]) => v)), [f]);
  const { data: options } = useQuery({ queryKey: ["filters"], queryFn: async () => (await api.get("/vehicles/filters")).data });
  const { data = [], isLoading } = useQuery({ queryKey: ["vehicles", query], queryFn: async () => (await api.get("/vehicles", { params: query })).data });
  const inventoryEmpty = !!options && options.makes.length === 0;
  useSEO({
    title: lang === "fr" ? "Véhicules d'exception à vendre — Palissiere Motor Group" : "Exceptional vehicles for sale — Palissiere Motor Group",
    description: lang === "fr" ? "Supercars, voitures de prestige et de collection sélectionnées par Palissiere Motor Group. Porsche, Ferrari, Lamborghini et plus." : "Supercars, prestige and collector cars selected by Palissiere Motor Group. Porsche, Ferrari, Lamborghini and more.",
    lang,
  });
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value, ...(k === "make" ? { model: "" } : {}) }));
  const models = f.make && options?.models?.[f.make] ? options.models[f.make] : [];

  return (
    <div className="bg-[#070809] pt-28 md:pt-36">
      <div className="container-x">
        <Reveal>
          <p className="eyebrow mb-5">{t("featured_eyebrow")}</p>
          <h1 className="display text-4xl sm:text-5xl lg:text-6xl" data-testid="vehicles-title">{t("veh_title")}</h1>
          <p className="mt-5 max-w-lg text-base text-[#8b95a5]">{t("veh_sub")}</p>
        </Reveal>
        {inventoryEmpty ? (
          <Reveal delay={0.1} className="grid gap-10 border-t border-white/10 py-16 lg:grid-cols-12 md:py-24" data-testid="inventory-empty-state">
            <div className="lg:col-span-7">
              <p className="display-tight text-xl leading-snug text-[#f4f5f7] sm:text-2xl lg:text-3xl" data-testid="inventory-empty-message">{t("inv_empty_msg")}</p>
              <p className="mt-6 max-w-md text-base text-[#8b95a5]">{t("inv_empty_sub")}</p>
            </div>
            <div className="flex flex-col gap-3 lg:col-span-4 lg:col-start-9 lg:pt-2">
              <Link to="/sourcing" data-testid="inventory-empty-cta-sourcing" className="btn btn-primary">{t("hero_source")} <ArrowRight size={14} /></Link>
              <Link to="/sell" data-testid="inventory-empty-cta-sell" className="btn btn-ghost">{t("hero_sell")}</Link>
            </div>
          </Reveal>
        ) : (
        <>
        <Reveal delay={0.1} className="mt-12 grid grid-cols-2 gap-x-6 gap-y-5 border-y border-white/10 py-6 lg:grid-cols-4 xl:grid-cols-8" data-testid="vehicle-filters">
          <Field label={t("filter_make")}>
            <Select data-testid="vehicle-filter-make" value={f.make} onChange={set("make")}>
              <option value="">{t("filter_all")}</option>
              {options?.makes?.map((m) => <option key={m} value={m}>{m}</option>)}
            </Select>
          </Field>
          <Field label={t("filter_model")}>
            <Select data-testid="vehicle-filter-model" value={f.model} onChange={set("model")} disabled={!f.make}>
              <option value="">{t("filter_all")}</option>
              {models.map((m) => <option key={m} value={m}>{m}</option>)}
            </Select>
          </Field>
          <Field label={`${t("filter_year")} min`}><Input data-testid="vehicle-filter-year-min" type="number" placeholder={options?.year_min || ""} value={f.year_min} onChange={set("year_min")} /></Field>
          <Field label={`${t("filter_year")} max`}><Input data-testid="vehicle-filter-year-max" type="number" placeholder={options?.year_max || ""} value={f.year_max} onChange={set("year_max")} /></Field>
          <Field label={`${t("filter_price")} (€)`}><Input data-testid="vehicle-filter-price" type="number" step="1000" value={f.price_max} onChange={set("price_max")} /></Field>
          <Field label={t("filter_mileage")}><Input data-testid="vehicle-filter-mileage" type="number" step="1000" value={f.mileage_max} onChange={set("mileage_max")} /></Field>
          <Field label={t("filter_status")}>
            <Select data-testid="vehicle-filter-status" value={f.status} onChange={set("status")}>
              <option value="">{t("filter_all")}</option>
              {["available", "reserved", "sold", "off_market", "coming_soon"].map((s) => <option key={s} value={s}>{t(`st_${s}`)}</option>)}
            </Select>
          </Field>
          <Field label={t("filter_type")}>
            <Select data-testid="vehicle-filter-type" value={f.vehicle_type} onChange={set("vehicle_type")}>
              <option value="">{t("filter_all")}</option>
              {TYPES.map((s) => <option key={s} value={s}>{t(`t_${s}`)}</option>)}
            </Select>
          </Field>
        </Reveal>
        <div className="mt-6 flex items-center justify-between">
          <span className="mono text-[0.6rem] text-[#8b95a5]" data-testid="vehicles-count">{data.length} {t("veh_count")}</span>
          <button data-testid="vehicle-filter-reset" onClick={() => setF(Object.fromEntries(KEYS.map((k) => [k, ""])))} className="mono link-underline text-[0.6rem] text-[#c7ccd4]">{t("filter_reset")}</button>
        </div>
        <div className="grid gap-x-8 gap-y-14 py-14 sm:grid-cols-2 lg:grid-cols-3 md:py-20" data-testid="vehicles-grid">
          {data.map((v, i) => (
            <Reveal key={v.id} delay={(i % 3) * 0.08} className={i % 5 === 0 ? "sm:col-span-2 lg:col-span-2" : ""}>
              <VehicleCard v={v} large={i % 5 === 0} />
            </Reveal>
          ))}
          {!isLoading && !data.length && <p className="text-[#8b95a5] sm:col-span-3" data-testid="vehicles-empty">{t("veh_empty")}</p>}
        </div>
        </>
        )}
      </div>
    </div>
  );
}
